// src/hooks/usePartners.ts
//
// react-query hooks for partners.
//
// `usePartners` is the supplier list, fetched once for the whole app. Five
// components under Outsourcing each ran this same query inline, under two
// different react-query keys ("inquiryPartners" and "requisitionPartners"), so
// the same 1000 partners were fetched twice per page load and a component that
// picked the wrong key silently opted out of the cache the page had already
// filled. One key, one hook, one request.
//
// `usePartnerList` is the Partner Overview's paged, searched, filtered view of
// the same rows (EE-272). Both sit under the `partnerKeys.all` prefix so a
// create/edit/delete on the overview invalidates every partner-derived list at
// once - the supplier dropdowns included, which used to keep showing a
// partner for up to ten minutes after it was deleted.
import { useContext } from "react";
import { useQuery, UseQueryOptions } from "react-query";
import AppStateContext from "contexts/AppStateContext";
import {
  PartnerListFiltersType,
  PartnerListType,
  PartnerType,
} from "types/partners/PartnerTypes";
import { apiGetPartners } from "util/network/Partners";

// The supplier dropdowns want the whole list to search or to resolve an id
// against - so one big page it is.
const PARTNER_PAGE_SIZE = 1000;

export const partnerKeys = {
  all: ["partners"] as const,
  // The unfiltered supplier list. Kept as the bare prefix key it always was.
  supplierList: () => ["partners"] as const,
  lists: () => [...partnerKeys.all, "list"] as const,
  list: (filters: PartnerListFiltersType) =>
    [...partnerKeys.lists(), filters] as const,
};

/**
 * The supplier list, or `undefined` until it arrives. Cached for 10 minutes:
 * partners change on human timescales, and every dashboard, filter bar and
 * form on the page asks for them at once.
 */
export const usePartners = (): PartnerType[] | undefined => {
  const { token } = useContext(AppStateContext);
  return useQuery<PartnerType[]>(
    partnerKeys.supplierList(),
    () =>
      apiGetPartners(token, { page: 1, pageSize: PARTNER_PAGE_SIZE }).then(
        (res) => res.items ?? []
      ),
    { enabled: !!token, staleTime: 10 * 60 * 1000 }
  ).data;
};

/**
 * One page of the Partner Overview. `keepPreviousData` so the table keeps its
 * rows while the next page/filter loads instead of flashing empty.
 */
export const usePartnerList = (
  token: string,
  filters: PartnerListFiltersType = {},
  options?: UseQueryOptions<PartnerListType, any>
) =>
  useQuery<PartnerListType, any>(
    partnerKeys.list(filters),
    () => apiGetPartners(token, filters),
    {
      keepPreviousData: true,
      enabled: !!token,
      ...options,
    }
  );
